// LUMINA · pages/TeachingDetailPage.jsx
// Phase ~130: vertical timeline stages, tabbed resources, categorized layout

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API = "/api";
const GD   = "#D4AF37";
const GDB  = "#D4AF37";
const GDL  = "#C9A84C";
const BG   = "#FDFBF7";
const TXT  = "rgba(0,0,0,0.82)";
const TXT2 = "rgba(0,0,0,0.50)";
const DARK = "#1a1008";

const TYPE_LABEL = {
  teaching:"教法", empowerment:"灌顶", retreat:"闭关",
  practice:"修法", text_study:"经典研习",
};
const TYPE_COLOR = {
  teaching:"#3b82f6", empowerment:"#dc2626", retreat:"#8b5cf6",
  practice:"#064E3B", text_study:"#d97706",
};

const FILE_ICON = { video:"🎥", audio:"🎧", pdf:"📄", docx:"📝", image:"🖼️", link:"🔗", text:"📋" };
const LANG_BADGE = { zh:"中", en:"EN", tib:"藏", multi:"多" };

// ── CredentialLockBanner ──
function CredentialLockBanner({ reason, uiLang }) {
  const isEn = uiLang === 'en';
  return (
    <div style={{
      border: '1.5px solid #D4AF37', borderRadius: 10,
      padding: '24px 28px', textAlign: 'center', background: '#FFFBF0',
      marginBottom: 28,
    }}>
      <div style={{ fontSize: 36 }}>🔒</div>
      <div style={{ fontWeight: 600, marginTop: 10, color: '#8B6914', fontSize: 15 }}>
        {reason || (isEn ? 'This teaching requires empowerment.' : '此教法需要灌顶')}
      </div>
      <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', marginTop: 8, lineHeight: 1.7 }}>
        {isEn
          ? 'Already received this empowerment? Email us to request access. Include: (1) empowerment date (2) teacher (3) lineage (4) location.'
          : '已获此灌顶？请发邮件申请资料访问权限。请提供：(1) 灌顶日期 (2) 上师 (3) 传承 (4) 地点'}
      </div>
      <a href="mailto:canada.btf@gmail.com?subject=申请资料访问：惹那林巴普巴金刚" style={{
        display: 'inline-block', marginTop: 14,
        background: '#D4AF37', color: '#fff',
        borderRadius: 6, padding: '9px 22px', textDecoration: 'none', fontSize: 14,
        fontWeight: 600,
      }}>
        {isEn ? 'Email to Request Access →' : '发邮件申请访问 →'}
      </a>
      <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.35)', marginTop: 8 }}>
        canada.btf@gmail.com
      </div>
    </div>
  );
}

// ── SectionTitle (with optional count badge) ──
function SectionTitle({ icon, label, color, count }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16, fontSize:12, color, fontWeight:600, letterSpacing:2, textTransform:"uppercase" }}>
      <span>{icon}</span>
      <span>{label}</span>
      {count !== undefined && (
        <span style={{ fontSize:10, color:"rgba(212,175,55,0.6)", background:"rgba(212,175,55,0.12)", borderRadius:10, padding:"1px 8px", marginLeft:-4 }}>{count}</span>
      )}
      <div style={{ flex:1, height:1, background:`linear-gradient(90deg, ${color}33, transparent)` }}/>
    </div>
  );
}

// ── ActionButton: determines what to show per resource ──
function ActionButton({ resource, vajraToken, navigate }) {
  if (resource.lock_reason) return <span style={{ fontSize:11, color:"#999", flexShrink:0 }}>🔒</span>;
  if (resource.can_download && resource.file_url) {
    const label = resource.file_type === 'video' ? '▶️ 观看' : resource.file_type === 'audio' ? '▶️ 收听' : '📄 打开';
    return (
      <a href={resource.file_url} target="_blank" rel="noopener noreferrer" style={{
        fontSize:11, color:GD, textDecoration:"none",
        border:"1px solid rgba(212,175,55,0.4)", borderRadius:6,
        padding:"4px 12px", flexShrink:0, whiteSpace:"nowrap",
        display:"inline-flex", alignItems:"center", gap:4,
      }}>{label}</a>
    );
  }
  if (resource.can_download && resource.download_token) {
    return (
      <a href={`${API}/vajra/download/${resource.download_token}`} style={{
        fontSize:11, color:GD, textDecoration:"none",
        border:"1px solid rgba(212,175,55,0.4)", borderRadius:6,
        padding:"4px 12px", flexShrink:0, whiteSpace:"nowrap",
      }}>下载 ↓</a>
    );
  }
  if (!vajraToken) {
    return <button onClick={() => navigate("/vajra?auth=1")} style={{
      fontSize:11, color:GD, background:"none",
      border:"1px solid rgba(212,175,55,0.4)", borderRadius:6,
      padding:"4px 12px", cursor:"pointer", flexShrink:0, whiteSpace:"nowrap",
    }}>登录查看 →</button>;
  }
  return <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)", flexShrink:0 }}>—</span>;
}

// ── ResourceCard: reusable component ──
function ResourceCard({ resource, uiLang, vajraToken, navigate }) {
  const isEn = uiLang === 'en';
  const title = isEn && resource.title_en ? resource.title_en : resource.title_zh;
  return (
    <div style={{
      display:"flex", justifyContent:"space-between", alignItems:"center",
      padding:"10px 14px", marginBottom:4,
      background:"#fff", borderRadius:8,
      border:"1px solid rgba(139,105,20,0.06)"
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, flex:1, minWidth:0 }}>
        <span style={{ fontSize:14, flexShrink:0 }}>{FILE_ICON[resource.file_type] || "📄"}</span>
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:12, color:TXT, fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{title}</div>
          <div style={{ display:"flex", gap:4, alignItems:"center", marginTop:2 }}>
            {resource.file_type && (
              <span style={{ fontSize:9, color:"rgba(212,175,55,0.6)", background:"rgba(212,175,55,0.12)", borderRadius:4, padding:"1px 5px", textTransform:"uppercase" }}>{resource.file_type}</span>
            )}
            {resource.language && (
              <span style={{ fontSize:9, color:"rgba(0,0,0,0.4)", background:"rgba(0,0,0,0.04)", borderRadius:4, padding:"1px 5px" }}>{LANG_BADGE[resource.language] || resource.language}</span>
            )}
            {resource.description && (
              <span style={{ fontSize:10, color:"rgba(0,0,0,0.4)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{resource.description}</span>
            )}
          </div>
        </div>
      </div>
      <ActionButton resource={resource} vajraToken={vajraToken} navigate={navigate} />
    </div>
  );
}

// ── ResourceGroup: category-grouped cards ──
function ResourceGroup({ groups, uiLang, emptyMsg, vajraToken, navigate }) {
  if (!groups || groups.length === 0) {
    return <div style={{ fontSize:12, color:"rgba(0,0,0,0.3)", textAlign:"center", padding:"20px 0", fontStyle:"italic" }}>{emptyMsg}</div>;
  }
  return (
    <div>
      {groups.map(g => (
        <div key={g.category} style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, color:GD, fontWeight:600, letterSpacing:1, marginBottom:6, textTransform:"uppercase" }}>
            {g.category} <span style={{ fontSize:9, color:"rgba(212,175,55,0.5)" }}>({g.items.length})</span>
          </div>
          {g.items.map(r => <ResourceCard key={r.id} resource={r} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} />)}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════
export default function TeachingDetailPage({ lang: propLang }) {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [teaching, setTeaching] = useState(null);
  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [uiLang,   setUiLang]   = useState(propLang || 'zh');
  const [expandedStages, setExpandedStages] = useState(new Set());
  const [resourceTab, setResourceTab] = useState('videos');

  const vajraToken = localStorage.getItem("vajra_token") || null;

  useEffect(() => {
    const headers = vajraToken ? { Authorization: `Bearer ${vajraToken}` } : {};
    fetch(`${API}/vajra/teachings/${id}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setTeaching(d);
        setLoading(false);
        if (d?.id) {
          fetch(`${API}/vajra/events?teaching_id=${d.id}`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(evs => setEvents(Array.isArray(evs) ? evs : []))
            .catch(() => {});
        }
      })
      .catch(() => setLoading(false));
  }, [id, vajraToken]);

  // Auto-expand all stages on load
  useEffect(() => {
    if (teaching?.stages) {
      setExpandedStages(new Set(teaching.stages.map(s => s.id)));
    }
  }, [teaching]);

  useEffect(() => {
    if (teaching) {
      document.title = `${uiLang === 'en' && teaching.title_en ? teaching.title_en : teaching.title_zh} — Vajra | BTF Canada`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.content = teaching.description_zh || teaching.title_zh;
    }
  }, [teaching, uiLang]);

  // ── Data Processing ──
  const resources = teaching?.resources || [];
  const stages = teaching?.stages || [];

  // Group videos by category
  const videosByCategory = useMemo(() => {
    const videos = resources.filter(r => r.file_type === 'video');
    const groups = {};
    videos.forEach(r => { const c = r.category || '教学视频'; if (!groups[c]) groups[c] = []; groups[c].push(r); });
    return Object.entries(groups).map(([category, items]) => ({ category, items }));
  }, [resources]);

  // Group PDFs by category
  const pdfsByCategory = useMemo(() => {
    const pdfs = resources.filter(r => r.file_type === 'pdf');
    const groups = {};
    pdfs.forEach(r => { const c = r.category || '其他'; if (!groups[c]) groups[c] = []; groups[c].push(r); });
    return Object.entries(groups).map(([category, items]) => ({ category, items }));
  }, [resources]);

  // Match resources to stages — stage_id first, fallback to category matching
  const stageResources = useMemo(() => {
    const map = {};
    stages.forEach(stage => {
      map[stage.id] = resources.filter(r => {
        if (r.stage_id != null) return r.stage_id === stage.id;
        // Legacy fallback: category-based matching
        if (!r.category) return false;
        return r.category === stage.name_zh
          || r.category === stage.name_en
          || (stage.name_zh && r.category.includes(stage.name_zh))
          || (r.category && stage.name_zh.includes(r.category));
      });
    });
    return map;
  }, [stages, resources]);

  // ── Render ──
  if (loading) return (
    <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", color:TXT2, fontSize:14 }}>载入中…</div>
  );
  if (!teaching) return (
    <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:32, marginBottom:12 }}>☸</div>
        <div style={{ color:TXT2, fontSize:14 }}>法教不存在或已下架</div>
        <button onClick={() => navigate("/vajra")} style={{ marginTop:16, padding:"10px 24px", borderRadius:8, border:`1px solid ${GD}`, background:"transparent", color:GD, fontSize:13, cursor:"pointer" }}>如意宝金刚社群</button>
      </div>
    </div>
  );

  const isLocked   = !!teaching.is_locked;
  const typeColor  = TYPE_COLOR[teaching.teaching_type] || GD;
  const typeLabel  = TYPE_LABEL[teaching.teaching_type] || teaching.teaching_type || "法教";
  const description = isLocked ? teaching.description_zh : (teaching.full_description || teaching.description_zh);
  const hasCover   = !!teaching.cover_image_url;

  const title = uiLang === 'en' && teaching.title_en ? teaching.title_en : teaching.title_zh;
  const desc  = uiLang === 'en' && teaching.description_en ? teaching.description_en : description;

  const totalSessions = events.reduce((sum, ev) => {
    try { return sum + (Array.isArray(JSON.parse(ev.sessions)) ? JSON.parse(ev.sessions).length : 0); }
    catch { return sum + 0; }
  }, 0);

  const toggleStage = (id) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div style={{ minHeight:"100vh", background:BG, fontFamily:"'Noto Serif SC', 'Noto Sans SC', serif" }}>

      {/* ═══ Breadcrumb ═══ */}
      <div style={{ padding:"10px 28px", fontSize:12, color:"rgba(0,0,0,0.45)", fontFamily:"'Noto Sans SC', sans-serif", background:BG, borderBottom:"1px solid rgba(139,105,20,0.12)" }}>
        <span style={{ maxWidth:800, display:"block", margin:"0 auto" }}>
          <a href="/vajra" style={{ color:GD, textDecoration:"none" }}>金刚社群</a>
          {' › '}
          <a href="/vajra/teachings" style={{ color:GD, textDecoration:"none" }}>教法</a>
          {teaching.topic_hub_slug && (
            <>{' › '}<a href={`/vajra/teachings/${teaching.topic_hub_slug}`} style={{ color:GD, textDecoration:"none" }}>{teaching.topic_hub_slug === 'vajrakilaya' ? '普巴金刚' : teaching.topic_hub_slug}</a></>
          )}
          {' › '}
          <span style={{ color:"rgba(0,0,0,0.55)" }}>{teaching.title_zh}</span>
        </span>
      </div>

      {/* ═══ Hero Banner ═══ */}
      <div style={{
        position:"relative", minHeight: hasCover ? 360 : 200,
        background: hasCover
          ? `url(${teaching.cover_image_url}) center/cover no-repeat`
          : `linear-gradient(160deg, ${DARK} 0%, #3d2a0f 40%, ${GD} 100%)`,
        display:"flex", alignItems:"flex-end",
      }}>
        <div style={{ position:"absolute", inset:0, background: hasCover ? "linear-gradient(180deg, rgba(26,16,8,0.35) 0%, rgba(26,16,8,0.75) 70%, rgba(26,16,8,0.92) 100%)" : "linear-gradient(180deg, transparent 0%, rgba(26,16,8,0.3) 100%)" }}/>
        <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg, transparent, ${GDB}, transparent)` }}/>
        {isLocked && (
          <div style={{ position:"absolute", top:16, right:16, background:"rgba(0,0,0,0.65)", borderRadius:8, padding:"6px 14px", color:"#fff", fontSize:12, display:"flex", alignItems:"center", gap:6, backdropFilter:"blur(4px)" }}>
            🔒 {teaching.lock_reason || '需要灌顶'}
          </div>
        )}
        <div style={{ position:"relative", zIndex:2, maxWidth:800, margin:"0 auto", width:"100%", padding:"48px 28px 32px" }}>
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:GDB, border:`1px solid ${GDB}55`, borderRadius:20, padding:"3px 12px", backdropFilter:"blur(4px)" }}>☸ {typeLabel}</span>
            {teaching.tradition && <span style={{ fontSize:11, color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:20, padding:"3px 12px", backdropFilter:"blur(4px)" }}>{teaching.tradition}</span>}
            {teaching.lineage && <span style={{ fontSize:11, color:"rgba(255,255,255,0.7)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:20, padding:"3px 12px", backdropFilter:"blur(4px)" }}>{teaching.lineage}</span>}
          </div>
          <h1 style={{ fontSize: hasCover ? 28 : 24, fontWeight:700, color:"#fff", margin:"0 0 8px", lineHeight:1.35, textShadow:"0 2px 12px rgba(0,0,0,0.4)", letterSpacing:1 }}>{teaching.title_zh}{teaching.title_en ? ' · '+teaching.title_en : ''}</h1>
          {(teaching.teacher || teaching.teacher_name) && (
            <div style={{ fontSize:14, color:GDB, marginTop:6, letterSpacing:.5 }}>🪷 {teaching.teacher?.name_zh || teaching.teacher_name}</div>
          )}
        </div>
      </div>

      {/* ═══ Sticky nav (scroll section links) ═══ */}
      <div style={{ position:"sticky", top:0, zIndex:50, background:"rgba(253,251,247,0.97)", backdropFilter:"blur(12px)", borderBottom:"1px solid rgba(212,175,55,0.25)", padding:"6px 28px" }}>
        <div style={{ maxWidth:800, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
          <button onClick={() => navigate(teaching.topic_hub_slug ? `/vajra/teachings/${teaching.topic_hub_slug}` : "/vajra/teachings")} style={{ background:"none", border:"none", color:GD, fontSize:18, cursor:"pointer", padding:"0 4px 0 0", flexShrink:0 }}>‹</button>
          <div style={{ display:"flex", gap:12, flex:1, justifyContent:"center", overflowX:"auto" }}>
            {['path','sessions','resources'].map(sec => (
              <a key={sec} href={`#section-${sec}`} style={{ fontSize:11, color:"rgba(253,251,247,0.5)", textDecoration:"none", whiteSpace:"nowrap", letterSpacing:1 }} onClick={(e) => { e.preventDefault(); document.getElementById(`section-${sec}`)?.scrollIntoView({ behavior:'smooth' }); }}>
                {sec === 'path' ? (uiLang === 'en' ? 'Path' : '修行路径') : sec === 'sessions' ? (uiLang === 'en' ? 'Sessions' : '课程安排') : (uiLang === 'en' ? 'Resources' : '修法资料')}
              </a>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
            {teaching.bilingual && (
              <button onClick={() => setUiLang(l => l === 'zh' ? 'en' : 'zh')} style={{ background:"none", border:`1px solid rgba(212,175,55,0.45)`, borderRadius:4, padding:"3px 8px", cursor:"pointer", color:GD, fontSize:11, fontWeight:600 }}>{uiLang === 'zh' ? 'EN' : '中'}</button>
            )}
            {!vajraToken && (
              <a href="/vajra" style={{ fontSize:10, color:GD, textDecoration:"none", border:"1px solid rgba(212,175,55,0.35)", borderRadius:6, padding:"3px 10px", whiteSpace:"nowrap" }}>{uiLang === 'en' ? 'Join →' : '登录加入 →'}</a>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════════════ Content ══════════════════════ */}
      <div style={{ maxWidth:800, margin:"0 auto", padding:"32px 28px 60px" }}>

        {/* ── 课程纲要 ── */}
        {desc && (
          <div style={{ marginBottom:32 }} id="section-overview">
            <SectionTitle icon="📖" label={uiLang === 'en' ? "Curriculum" : "课程纲要"} color={typeColor} />
            <div style={{ fontSize:14, color:TXT2, lineHeight:2, background:"rgba(0,0,0,0.04)", borderRadius:12, padding:"20px 24px", border:"1px solid rgba(139,105,20,0.12)" }}>
              {desc.split("\n").filter(p => p.trim()).map((p, i) => {
                const isHeading = p.startsWith("【") || p.startsWith("📖") || p.startsWith("⚠") || p.startsWith("🔒");
                return <p key={i} style={{ margin:"0 0 8px", fontWeight: isHeading ? 600 : 400, color: isHeading ? TXT : TXT2, fontSize: isHeading ? 15 : 14, borderLeft: isHeading ? `3px solid ${GDB}` : "none", paddingLeft: isHeading ? 12 : 0 }}>{p}</p>;
              })}
            </div>
          </div>
        )}

        {/* ── 修习要求 ── */}
        {teaching.prerequisite_zh && (
          <div style={{ marginBottom:28, padding:"14px 18px", borderRadius:10, background:"rgba(212,175,55,0.06)", border:"1px solid rgba(212,175,55,0.18)" }}>
            <div style={{ fontSize:10, color:GD, letterSpacing:1.5, marginBottom:6, fontWeight:600 }}>{uiLang === 'en' ? 'PREREQUISITES' : '修习要求'}</div>
            <div style={{ fontSize:12, color:TXT2, lineHeight:1.8 }}>{teaching.prerequisite_zh}</div>
          </div>
        )}

        {/* ═══ SECTION 1: 修行路径 — Vertical Timeline ═══ */}
        {stages.length > 0 && (
          <div style={{ marginBottom:32 }} id="section-path">
            <SectionTitle icon="🛤️" label={uiLang === 'en' ? `Practice Path (${stages.length})` : `修行路径 (${stages.length})`} color={typeColor} count={stages.length} />
            <div style={{ position:"relative", paddingLeft:28 }}>
              {/* Vertical line */}
              <div style={{ position:"absolute", left:11, top:12, bottom:12, width:2, background:`linear-gradient(180deg, ${GDB}66, ${GDB}11)`, borderRadius:1 }}/>
              {stages.map((stage, i) => {
                const isExpanded = expandedStages.has(stage.id);
                const sResources = stageResources[stage.id] || [];
                const sVideos = sResources.filter(r => r.file_type === 'video');
                const sAudios = sResources.filter(r => r.file_type === 'audio');
                const sPdfs   = sResources.filter(r => r.file_type === 'pdf');
                const sOthers = sResources.filter(r => !['video','audio','pdf'].includes(r.file_type));
                return (
                  <div key={stage.id} style={{ marginBottom:10 }}>
                    {/* Timeline dot */}
                    <div style={{ position:"absolute", left:11, width:10, height:10, borderRadius:"50%", background:typeColor, border:`2px solid ${BG}`, boxShadow:`0 0 0 2px ${typeColor}44`, transform:"translateX(-50%)", marginTop:18 }}/>
                    {/* Stage card */}
                    <div style={{ background:"#fff", borderRadius:10, overflow:"hidden", border:"1px solid rgba(139,105,20,0.08)" }}>
                      {/* Header */}
                      <div onClick={() => toggleStage(stage.id)} style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:10, cursor:"pointer", borderBottom: isExpanded ? "1px solid rgba(212,175,55,0.08)" : "none" }}>
                        <span style={{ fontSize:10, fontWeight:700, color:GDB, minWidth:20, textAlign:"center", fontFeatureSettings:"'tnum' 1" }}>{String(i+1).padStart(2,'0')}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <span style={{ fontSize:13, color:TXT, fontWeight:600 }}>{uiLang === 'en' && stage.name_en ? stage.name_en : stage.name_zh}</span>
                          {stage.name_en && uiLang !== 'en' && <span style={{ fontSize:10, color:"rgba(253,251,247,0.4)", marginLeft:8 }}>{stage.name_en}</span>}
                        </div>
                        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                          {stage.is_required && <span style={{ fontSize:9, color:"#8B6914", background:"rgba(212,175,55,0.12)", borderRadius:4, padding:"1px 6px", fontWeight:600 }}>必修</span>}
                          <span style={{ fontSize:10, color:GDB, transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.2s" }}>▼</span>
                        </div>
                      </div>
                      {/* Body */}
                      {isExpanded && (
                        <div style={{ padding:"10px 16px 14px" }}>
                          {stage.description_zh && (
                            <div style={{ fontSize:12, color:TXT2, lineHeight:1.7, marginBottom:10, padding:"8px 12px", background:"rgba(212,175,55,0.04)", borderRadius:8 }}>{stage.description_zh}</div>
                          )}
                          {sVideos.length > 0 && (
                            <div style={{ marginBottom:8 }}>
                              <div style={{ fontSize:9, color:GDB, fontWeight:600, letterSpacing:1, marginBottom:4 }}>🎥 {uiLang==='en'?'VIDEOS':'视频'} ({sVideos.length})</div>
                              {sVideos.map(r => <ResourceCard key={r.id} resource={r} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} />)}
                            </div>
                          )}
                          {sAudios.length > 0 && (
                            <div style={{ marginBottom:8 }}>
                              <div style={{ fontSize:9, color:GDB, fontWeight:600, letterSpacing:1, marginBottom:4 }}>🎧 {uiLang==='en'?'AUDIO':'音频'} ({sAudios.length})</div>
                              {sAudios.map(r => <ResourceCard key={r.id} resource={r} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} />)}
                            </div>
                          )}
                          {sPdfs.length > 0 && (
                            <div style={{ marginBottom:8 }}>
                              <div style={{ fontSize:9, color:GDB, fontWeight:600, letterSpacing:1, marginBottom:4 }}>📄 {uiLang==='en'?'TEXTS':'文本'} ({sPdfs.length})</div>
                              {sPdfs.map(r => <ResourceCard key={r.id} resource={r} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} />)}
                            </div>
                          )}
                          {sOthers.length > 0 && (
                            <div>
                              <div style={{ fontSize:9, color:GDB, fontWeight:600, letterSpacing:1, marginBottom:4 }}>📋 {uiLang==='en'?'OTHER':'其他'} ({sOthers.length})</div>
                              {sOthers.map(r => <ResourceCard key={r.id} resource={r} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} />)}
                            </div>
                          )}
                          {sVideos.length === 0 && sAudios.length === 0 && sPdfs.length === 0 && sOthers.length === 0 && (
                            <div style={{ fontSize:11, color:"rgba(0,0,0,0.3)", fontStyle:"italic", padding:"4px 0" }}>{uiLang==='en'?'No resources linked yet.':'暂无相关资料'}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ SECTION 2: 课程安排 ═══ */}
        {events.length > 0 && (
          <div style={{ marginBottom:32 }} id="section-sessions">
            <SectionTitle icon="📅" label={uiLang === 'en' ? `Sessions (${totalSessions})` : `课程安排 (${totalSessions}场)`} color={GDB} />
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {events.map(ev => {
                const sessions = (() => { try { return JSON.parse(ev.sessions || '[]'); } catch { return []; } })();
                return (
                  <div key={ev.id} style={{ background:"#fff", borderRadius:10, border:"1px solid rgba(139,105,20,0.08)", overflow:"hidden" }}>
                    <div style={{ padding:"14px 18px", borderBottom:"1px solid rgba(212,175,55,0.08)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:10, color:GDB, marginBottom:2, fontWeight:600 }}>{ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` – ${ev.end_date}` : ''}</div>
                        <div style={{ fontSize:13, color:TXT, fontWeight:600 }}>{uiLang === 'en' && ev.title_en ? ev.title_en : ev.title_zh}</div>
                      </div>
                      <span style={{ fontSize:10, color:GDB, border:`1px solid ${GDB}44`, borderRadius:12, padding:"2px 8px" }}>{sessions.length}场</span>
                    </div>
                    <div style={{ padding:"8px 18px" }}>
                      {sessions.map((s, i) => {
                        const stageNum = s.title_zh?.match(/第([一二三四五六七八九十\d]+)讲/);
                        return (
                          <div key={i} style={{ display:"grid", gridTemplateColumns:"70px 80px 1fr", gap:8, alignItems:"center", padding:"6px 0", fontSize:12, borderBottom: i < sessions.length-1 ? "1px solid rgba(212,175,55,0.04)" : "none" }}>
                            <span style={{ fontSize:10, color:GDB, fontFeatureSettings:"'tnum' 1" }}>{s.date}</span>
                            <span style={{ fontSize:10, color:"rgba(253,251,247,0.4)", fontFeatureSettings:"'tnum' 1" }}>{s.day_zh} {s.time}</span>
                            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <span style={{ color:TXT, lineHeight:1.4 }}>{s.title_zh}</span>
                              {stageNum && <span style={{ fontSize:9, color:typeColor, background:`${typeColor}15`, borderRadius:4, padding:"1px 5px", whiteSpace:"nowrap" }}>{stageNum[0]}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {ev.online_link && (
                      <div style={{ padding:"8px 18px", background:"rgba(212,175,55,0.05)", borderTop:"1px solid rgba(212,175,55,0.08)" }}>
                        <a href={ev.online_link} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:GD, textDecoration:"none" }}>🔗 {uiLang === 'en' ? 'Join Online' : '在线参加'}</a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ SECTION 3: 修法资料 (tabbed) ═══ */}
        {isLocked ? (
          <CredentialLockBanner reason={teaching.lock_reason} uiLang={uiLang} />
        ) : resources.length > 0 && (
          <div style={{ marginBottom:28 }} id="section-resources">
            <SectionTitle icon="📂" label={uiLang === 'en' ? `Resources (${resources.length})` : `修法资料 (${resources.length})`} color={GD} count={resources.length} />
            {/* Tabs */}
            <div style={{ display:"flex", gap:4, marginBottom:14, overflowX:"auto" }}>
              {[
                { key:'videos',  icon:'🎥', zh:'教学视频', en:'Videos',  count:videosByCategory.reduce((s,g)=>s+g.items.length,0) },
                { key:'rituals', icon:'📜', zh:'仪轨文本', en:'Rituals', count:pdfsByCategory.reduce((s,g)=>s+g.items.length,0) },
                { key:'all',     icon:'📋', zh:'全部',    en:'All' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setResourceTab(tab.key)} style={{
                  fontSize:11, fontWeight:500, padding:"5px 12px", borderRadius:20, border:"none", cursor:"pointer", whiteSpace:"nowrap",
                  background: resourceTab === tab.key ? `${GD}22` : "rgba(0,0,0,0.04)",
                  color: resourceTab === tab.key ? GD : "rgba(0,0,0,0.45)",
                  transition:"all 0.15s",
                }}>
                  {tab.icon} {uiLang==='en'?tab.en:tab.zh}
                  {tab.count !== undefined && <span style={{ fontSize:9, marginLeft:3, opacity:0.7 }}>({tab.count})</span>}
                </button>
              ))}
            </div>
            {/* Tab Content */}
            {resourceTab === 'videos' && <ResourceGroup groups={videosByCategory} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} emptyMsg={uiLang==='en'?'No videos yet.':'暂无教学视频'} />}
            {resourceTab === 'rituals' && <ResourceGroup groups={pdfsByCategory} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} emptyMsg={uiLang==='en'?'No ritual texts yet.':'暂无仪轨文本'} />}
            {resourceTab === 'all' && (
              <div>{resources.map(r => <ResourceCard key={r.id} resource={r} uiLang={uiLang} vajraToken={vajraToken} navigate={navigate} />)}</div>
            )}
          </div>
        )}

        {/* ── 材料说明 ── */}
        {!isLocked && teaching.materials_note && (
          <div style={{ fontSize:12, color:TXT2, lineHeight:1.7, padding:"12px 16px", background:"rgba(212,175,55,0.06)", borderRadius:8, border:"1px solid rgba(139,105,20,0.12)", marginBottom:28 }}>📋 {teaching.materials_note}</div>
        )}

        {/* ── CTA ── */}
        {!vajraToken && (
          <div style={{ marginTop:8, padding:"28px 24px", borderRadius:14, background:"rgba(212,175,55,0.12)", textAlign:"center", border:"1px solid rgba(212,175,55,0.2)" }}>
            <div style={{ fontSize:14, color:TXT, marginBottom:16, lineHeight:1.8 }}>
              {uiLang === 'en' ? 'Join the Vajra Community for complete practice resources & guru guidance' : '加入金刚社群，获取完整修法资料与上师指导'}
            </div>
            <button onClick={() => navigate("/vajra")} style={{ padding:"12px 32px", borderRadius:8, background:`linear-gradient(135deg, ${GD}, #A07828)`, color:"#fff", border:"none", fontSize:14, fontWeight:600, cursor:"pointer", letterSpacing:1, boxShadow:"0 4px 14px rgba(139,105,20,0.25)" }}>{uiLang === 'en' ? 'Login / Join →' : '登录 / 加入社群 →'}</button>
          </div>
        )}
      </div>

      {/* ═══ Footer ═══ */}
      <div style={{ textAlign:"center", padding:"28px 0 20px", fontSize:11, color:"rgba(139,105,20,0.4)", borderTop:"1px solid rgba(139,105,20,0.08)" }}>
        © 2026 显密文教基金会 · BTF Canada · Toronto<br/>联系：canada.btf@gmail.com
      </div>
    </div>
  );
}
